/*
 * help.js — Interaktion der Hilfe-/Service-Seite (sections/main-page-hilfe.liquid).
 * Reines Theme-JS, keine App, keine Abhängigkeiten. Progressive Enhancement:
 *  - Suche: native <form> → /search funktioniert ohne JS; JS ergänzt Autocomplete (inline + Mobile-Overlay)
 *           über einen kuratierten Inline-Index (FAQ-Fragen + Themen + Quicklinks). WAI-ARIA-Combobox.
 *  - FAQ:   <details> öffnen/schließen ist JS-frei; JS schaltet nur die Tab-Panels.
 *  - Status: Online/Offline aus Servicezeiten Mo–Fr 08–18 Uhr (Europe/Berlin); Initial serverseitig gesetzt.
 *  - Chat:  öffnet Crisp ($crisp.push(["do","chat:open"])).
 *  - Feedback: consent-neutrales DOM-Event 'help:feedback'. analytics.js reicht es an die
 *    Web-Pixels-Ebene weiter; das Consent-Gating erfolgt DORT gemäß Pixel-Permission
 *    (Admin → Einstellungen → Kundenereignisse, Custom Pixel auf „Berechtigung erforderlich").
 */
(function () {
  'use strict';
  // Idempotent: help.js wird global (theme.liquid) UND von der Hilfe-Section geladen — nur 1× ausführen.
  if (window.HomediaHelp) return;
  window.HomediaHelp = true;

  var ICON_SEARCH = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false" class="hm-icon"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.2-3.2"/></svg>';

  function escHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------------- Crisp Live-Chat ---------------- */
  // Der Chat-Button ist standardmäßig AUSGEBLENDET und erscheint erst, wenn der Nutzer den
  // Chat über einen [data-help-chat]/[data-door-check]-Trigger öffnet. Ab der ersten Öffnung
  // bleibt er per localStorage-Flag auch nach einem Reload sichtbar. Zusätzlich: z-index in
  // die Theme-Skala (container:index = --z-chat) + flackerfrei — base.css blendet
  // .crisp-client aus, bis wir html.hm-chat-ready setzen (auf session:loaded, sonst Timeout).
  var CHAT_KEY = 'hm-chat-open';
  function chatStarted() {
    try { return localStorage.getItem(CHAT_KEY) === '1'; } catch (e) { return false; }
  }
  function markChatStarted() {
    try { localStorage.setItem(CHAT_KEY, '1'); } catch (e) {}
  }
  function revealChat() { document.documentElement.classList.add('hm-chat-ready'); }
  try {
    window.$crisp = window.$crisp || [];
    var zChat = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--z-chat'), 10);
    window.$crisp.push(['config', 'container:index', [zChat > 0 ? zChat : 84]]);
    // Jede Öffnung (egal welcher Trigger) merkt sich „gestartet" und gibt den Chat frei.
    window.$crisp.push(['on', 'chat:opened', function () { markChatStarted(); revealChat(); }]);
    // Crisp fertig geladen → freigeben (danach steuert Crisps eigener show/hide-State).
    window.$crisp.push(['on', 'session:loaded', revealChat]);
    // Beim Laden nur zeigen, wenn der Nutzer den Chat schon einmal geöffnet hat.
    window.$crisp.push(['do', chatStarted() ? 'chat:show' : 'chat:hide']);
    // Fallback, falls session:loaded ausbleibt (Chat bleibt sonst dauerhaft unsichtbar).
    window.setTimeout(revealChat, 2500);
  } catch (e) {}

  function openCrisp() {
    try {
      window.$crisp = window.$crisp || [];
      markChatStarted();
      revealChat();
      window.$crisp.push(['do', 'chat:show']);
      window.$crisp.push(['do', 'chat:open']);
    } catch (e) {}
  }
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-help-chat]');
    if (t) { e.preventDefault(); openCrisp(); }
  });

  /* ---------------- Online-Status (Mo–Fr 08–18, Europe/Berlin) ---------------- */
  function serviceOpen() {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Europe/Berlin', weekday: 'short', hour: 'numeric', hour12: false
      }).formatToParts(new Date());
      var wd = '', hr = 0;
      parts.forEach(function (p) {
        if (p.type === 'weekday') wd = p.value;
        else if (p.type === 'hour') hr = parseInt(p.value, 10);
      });
      if (isNaN(hr)) hr = 0;
      hr = hr % 24;
      return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].indexOf(wd) >= 0 && hr >= 8 && hr < 18;
    } catch (e) {
      return true; /* fail-open: Kanal nie fälschlich als „offline" verstecken */
    }
  }
  function refreshStatus() {
    var open = serviceOpen();
    document.querySelectorAll('[data-help-status]').forEach(function (el) {
      el.classList.toggle('is-online', open);
      el.classList.toggle('is-offline', !open);
      var label = el.querySelector('[data-help-status-label]');
      if (label) {
        label.textContent = open
          ? (el.getAttribute('data-open-label') || 'Online')
          : (el.getAttribute('data-closed-label') || 'Offline');
      }
    });
  }

  /* ---------------- FAQ-Tabs ---------------- */
  function wireTabs() {
    document.querySelectorAll('[data-help-tabs]').forEach(function (tabs) {
      tabs.addEventListener('click', function (e) {
        var btn = e.target.closest && e.target.closest('[data-help-tab]');
        if (!btn) return;
        var key = btn.getAttribute('data-help-tab');
        tabs.querySelectorAll('[data-help-tab]').forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        document.querySelectorAll('[data-help-panel]').forEach(function (p) {
          p.hidden = p.getAttribute('data-help-panel') !== key;
        });
      });
    });
  }

  /* ---------------- Feedback (👍/👎) ---------------- */
  function recordVote(id, value, thanksEl) {
    try {
      var key = 'hm_help_vote_' + id;
      if (!window.localStorage || !localStorage.getItem(key)) {
        if (window.localStorage) localStorage.setItem(key, value);
        document.dispatchEvent(new CustomEvent('help:feedback', {
          detail: { id: id, value: value, page: location.pathname }
        }));
      }
    } catch (e) {}
    if (thanksEl) thanksEl.hidden = false;
  }
  document.addEventListener('click', function (e) {
    var fb = e.target.closest && e.target.closest('[data-help-feedback-vote]');
    if (fb) {
      var box = fb.closest('[data-help-feedback]');
      var id = (box && box.getAttribute('data-id')) || 'page';
      var value = fb.getAttribute('data-help-feedback-vote');
      // 👍 → Danke; 👎 → Folge-Link zum Kontaktformular, damit das Feedback uns wirklich erreicht.
      var downEl = box && box.querySelector('[data-help-feedback-down]');
      var reactEl = (value === 'down' && downEl) ? downEl : (box && box.querySelector('.hm-help__feedback-thanks'));
      recordVote('fb-' + id, value, reactEl);
      if (box) {
        box.querySelectorAll('[data-help-feedback-vote]').forEach(function (b) { b.hidden = true; });
      }
      return;
    }
    var v = e.target.closest && e.target.closest('[data-help-vote]');
    if (v) {
      var grp = v.closest('[data-help-vote-group]');
      var fid = (grp && grp.getAttribute('data-id')) || 'faq';
      recordVote('faq-' + fid, v.getAttribute('data-help-vote'),
        grp && grp.querySelector('.hm-help__vote-thanks'));
      if (grp) {
        // Nach dem ersten Vote sperren → sichtbarer und gemeldeter Zustand bleiben konsistent.
        grp.querySelectorAll('[data-help-vote]').forEach(function (b) {
          b.setAttribute('aria-pressed', b === v ? 'true' : 'false');
          b.disabled = true;
        });
      }
    }
  });

  /* ---------------- Suche (Autocomplete + Overlay) ---------------- */
  var INDEX = [];
  var indexEl = document.querySelector('[data-help-index]');
  if (indexEl) {
    try { INDEX = JSON.parse(indexEl.textContent) || []; } catch (e) { INDEX = []; }
  }
  var searchRoot = document.querySelector('[data-help-search]');
  var SEARCH_URL = (searchRoot && searchRoot.getAttribute('data-search-url')) || '/search';

  // Gruppentitel der Vorschläge: aus Liquid via data-Attribute (locales/*.json, help.search.*);
  // deutsche Literale bleiben als letzte Sicherheit, falls die Attribute fehlen.
  var labelEl = searchRoot || document.querySelector('[data-help-overlay]');
  function label(attr, fallback) {
    return (labelEl && labelEl.getAttribute(attr)) || fallback;
  }
  var L_POPULAR = label('data-label-popular', 'Beliebte Themen');
  var L_SUGGEST = label('data-label-suggestions', 'Vorschläge');
  var L_NO_RESULTS = label('data-label-noresults', 'Keine direkten Treffer – beliebte Themen');

  function gotoSearch(q) {
    if (!q) return;
    var sep = SEARCH_URL.indexOf('?') >= 0 ? '&' : '?';
    window.location.href = SEARCH_URL + sep + 'q=' + encodeURIComponent(q) + '&type=article,page';
  }

  function renderSuggest(container, q, titleDefault, titleQuery, idbase) {
    if (!container) return;
    var base = idbase || 'hm-help-opt';
    var query = (q || '').trim();
    var lq = query.toLowerCase();
    var list, title;
    if (!lq) { list = INDEX.slice(0, 6); title = titleDefault; }
    else {
      // Treffer über Titel UND hinterlegte Synonyme/Keywords (it.s) — kuratierte Suche, keine Volltextsuche.
      list = INDEX.filter(function (it) {
        return ((it.t || '') + ' ' + (it.s || '')).toLowerCase().indexOf(lq) >= 0;
      }).slice(0, 7);
      if (list.length) { title = titleQuery; }
      else { list = INDEX.slice(0, 6); title = L_NO_RESULTS; }
    }
    var html = '<div class="hm-help__sug-title">' + escHtml(title) + '</div>';
    list.forEach(function (it, i) {
      html += '<a class="hm-help__sug" id="' + base + '-' + i + '" href="' + escHtml(it.u) + '" role="option">' + ICON_SEARCH + '<span>' + escHtml(it.t) + '</span></a>';
    });
    // Bewusst KEIN „Alle Ergebnisse"-Link mehr → keine Weiterleitung auf die allgemeine /search-Seite.
    container.innerHTML = html;
    container.hidden = false;
  }

  function moveActive(container, dir) {
    var items = Array.prototype.slice.call(container.querySelectorAll('.hm-help__sug'));
    if (!items.length) return null;
    var idx = items.findIndex(function (i) { return i.classList.contains('is-active'); });
    if (idx >= 0) items[idx].classList.remove('is-active');
    idx = (idx + dir + items.length) % items.length;
    items[idx].classList.add('is-active');
    items[idx].scrollIntoView({ block: 'nearest' });
    return items[idx].id || null;
  }

  function wireSearch(input, container, idbase, onEscape) {
    if (!input || !container) return;
    var tD = L_POPULAR, tQ = L_SUGGEST;
    function show() {
      renderSuggest(container, input.value, tD, tQ, idbase);
      input.setAttribute('aria-expanded', 'true');
      input.removeAttribute('aria-activedescendant');
    }
    function hide() {
      container.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }
    input.addEventListener('input', show);
    input.addEventListener('focus', show);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { hide(); if (onEscape) onEscape(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); var id1 = moveActive(container, 1); if (id1) input.setAttribute('aria-activedescendant', id1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); var id2 = moveActive(container, -1); if (id2) input.setAttribute('aria-activedescendant', id2); }
      else if (e.key === 'Enter') {
        // Kein Weiterleiten auf /search: aktiven Vorschlag öffnen, sonst nur die Vorschläge zeigen.
        e.preventDefault();
        var act = container.querySelector('.hm-help__sug.is-active');
        if (act) { act.click(); }
        else { show(); }
      }
    });
  }

  // inline (Desktop)
  var inlineInput = searchRoot && searchRoot.querySelector('[data-help-search-input]');
  var inlineSuggest = searchRoot && searchRoot.querySelector('[data-help-suggest]');
  wireSearch(inlineInput, inlineSuggest, 'hm-help-opt-d', null);
  // „Suchen"/Submit leitet NICHT mehr auf die allgemeine /search-Seite, sondern zeigt die Vorschläge.
  var inlineForm = searchRoot && searchRoot.querySelector('.hm-help__search-form');
  if (inlineForm && inlineInput) {
    inlineForm.addEventListener('submit', function (e) {
      e.preventDefault();
      inlineInput.focus();
      inlineInput.dispatchEvent(new Event('input'));
    });
  }
  document.addEventListener('click', function (e) {
    if (searchRoot && inlineSuggest && !searchRoot.contains(e.target)) {
      inlineSuggest.hidden = true;
      if (inlineInput) { inlineInput.setAttribute('aria-expanded', 'false'); inlineInput.removeAttribute('aria-activedescendant'); }
    }
  });

  // overlay (Mobile) — modaler Dialog mit Escape, Fokus-Rückgabe und leichtem Fokus-Trap
  var overlay = document.querySelector('[data-help-overlay]');
  var overlayInput = overlay && overlay.querySelector('[data-help-search-input]');
  var overlaySuggest = overlay && overlay.querySelector('[data-help-suggest]');
  var overlayOpener = null;
  function onOverlayKey(e) {
    if (e.key === 'Escape') { closeOverlay(); return; }
    if (e.key === 'Tab' && overlay) {
      var f = Array.prototype.filter.call(
        overlay.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"])'),
        function (el) { return el.offsetParent !== null && !el.disabled; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  function openOverlay() {
    if (!overlay) return;
    overlayOpener = document.activeElement;
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onOverlayKey);
    if (overlayInput) { overlayInput.focus(); renderSuggest(overlaySuggest, overlayInput.value, L_POPULAR, L_SUGGEST, 'hm-help-opt-m'); }
  }
  function closeOverlay() {
    if (!overlay) return;
    overlay.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onOverlayKey);
    if (overlayOpener && overlayOpener.focus) { overlayOpener.focus(); }
    overlayOpener = null;
  }
  wireSearch(overlayInput, overlaySuggest, 'hm-help-opt-m', closeOverlay);
  document.querySelectorAll('[data-help-search-open]').forEach(function (b) { b.addEventListener('click', openOverlay); });
  document.querySelectorAll('[data-help-search-close]').forEach(function (b) { b.addEventListener('click', closeOverlay); });

  // Beliebt-Chips
  document.querySelectorAll('[data-help-chip]').forEach(function (c) {
    c.addEventListener('click', function () {
      var q = c.getAttribute('data-help-chip');
      if (inlineInput && inlineInput.offsetParent !== null) {
        inlineInput.value = q; inlineInput.focus();
        renderSuggest(inlineSuggest, q, L_POPULAR, L_SUGGEST, 'hm-help-opt-d');
      } else if (overlay) {
        openOverlay();
        if (overlayInput) { overlayInput.value = q; renderSuggest(overlaySuggest, q, L_POPULAR, L_SUGGEST, 'hm-help-opt-m'); }
      } else {
        gotoSearch(q);
      }
    });
  });

  /* ---------------- init ---------------- */
  function initPanels() {
    var activeBtn = document.querySelector('[data-help-tab].is-active');
    if (!activeBtn) return;
    var key = activeBtn.getAttribute('data-help-tab');
    document.querySelectorAll('[data-help-panel]').forEach(function (p) {
      p.hidden = p.getAttribute('data-help-panel') !== key;
    });
  }
  wireTabs();
  initPanels();
  refreshStatus();
  // Timer nur dort, wo es einen Live-Status gibt (help.js läuft jetzt global → kein Leerlauf-Intervall sonst).
  if (document.querySelector('[data-help-status]')) setInterval(refreshStatus, 60000);
})();
